import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { types as sdkTypes } from '../../util/sdkLoader';
import { customFormatMoney } from '../../util/currency';
import { useIntl } from '../../util/reactIntl';
import { packagePartLabel } from '../../util/packageBundleCore';
import { quantityLabel } from '../../util/orderQuantity';
import {
  getCart,
  removeFromCart,
  clearCart,
  groupCartByVendor,
  subscribeToCart,
  updateCartQuantity,
} from '../../util/cart';
import { buyNow, buyVendorCart, SELLER_NOT_PAYABLE } from '../../ducks/cart.duck';

import { Menu, MenuLabel, MenuContent, MenuItem, NamedLink } from '../../components';

import css from './CartMenu.module.css';

const { Money } = sdkTypes;

const CartIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const formatItemPrice = (intl, price, quantity = 1) => {
  if (!price || price.amount == null || !price.currency) return null;
  try {
    return customFormatMoney(intl, new Money(price.amount * quantity, price.currency));
  } catch (e) {
    return null;
  }
};

/**
 * P25009-474 BR-1 — the quantity on one flyout row. A per-item line gets a
 * stepper inside its stored limits (stock); a set line is locked at one set
 * (BR-4) and says so. A line stored before limits existed shows its count only
 * — the cart page refreshes its limits on the next visit.
 *
 * The buttons use aria-disabled, not disabled: a focused button that becomes
 * disabled drops focus to the page, and the Menu closes on that blur.
 */
const RowQuantity = ({ item, onChange }) => {
  const qty = item.orderData?.quantity || 1;
  const limits = item.limits;
  if (!limits || limits.locked) {
    return <span className={css.itemQuantity}>{quantityLabel(limits, qty)}</span>;
  }
  const atMin = qty <= (limits.min || 1);
  const atMax = qty >= limits.max;
  const step = (e, delta) => {
    e.preventDefault();
    e.stopPropagation();
    if ((delta < 0 && atMin) || (delta > 0 && atMax)) return;
    onChange(qty + delta);
  };
  return (
    <span className={css.stepper}>
      <button
        type="button"
        className={css.stepperButton}
        aria-label="Minska antal"
        aria-disabled={atMin}
        onClick={e => step(e, -1)}
      >
        −
      </button>
      <span className={css.stepperValue} aria-live="polite">
        {quantityLabel(limits, qty)}
      </span>
      <button
        type="button"
        className={css.stepperButton}
        aria-label="Öka antal"
        aria-disabled={atMax}
        onClick={e => step(e, 1)}
      >
        +
      </button>
    </span>
  );
};

/**
 * Cart (varukorg) flyout used in both the desktop account cluster and the
 * mobile action row. Reads the localStorage cart (util/cart), shows the
 * items grouped per seller with a per-line "Köp nu" that routes that single
 * listing into the existing single-vendor checkout, plus a per-seller
 * "Gå till kassan" that bundles all of one seller's items into one order.
 */
const CartMenu = props => {
  const { className, iconClassName } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();

  // Start empty so SSR markup matches the first client render; hydrate
  // from localStorage after mount, then keep in sync via subscription.
  const [items, setItems] = useState([]);
  const [buyingId, setBuyingId] = useState(null);
  const [vendorBusy, setVendorBusy] = useState(null);
  // Set when a checkout was refused because the seller can't take payment
  // yet (P25009-452 BR-2) — silence here would look like a dead button.
  const [notPayable, setNotPayable] = useState(false);

  useEffect(() => {
    setItems(getCart());
    const unsubscribe = subscribeToCart(setItems);
    return unsubscribe;
  }, []);

  // A checkout/transaction is single-vendor, so each seller's items check
  // out together (one payment). Cross-vendor carts are separate checkouts.
  const vendorList = groupCartByVendor(items);

  const count = items.length;
  const badge = count > 0 ? (count > 99 ? '99+' : String(count)) : null;

  // Note: we do NOT clear the cart on handoff to checkout — the buyer may go
  // back without paying. Purchased items are removed only after the payment
  // actually succeeds (wired in the post-payment step). On success the thunk
  // navigates away, so this component unmounts and the busy flag is moot.
  const handleBuyNow = item => {
    if (buyingId) return;
    setBuyingId(item.listingId);
    setNotPayable(false);
    dispatch(buyNow(item.listingId, item.orderData, history, routeConfiguration, config)).catch(
      e => {
        // Error already stored in the duck; just release the button.
        setBuyingId(null);
        if (e?.code === SELLER_NOT_PAYABLE) setNotPayable(true);
      }
    );
  };

  const handleVendorCheckout = group => {
    if (vendorBusy || !group?.items?.length) return;
    setVendorBusy(group.authorId || 'unknown');
    setNotPayable(false);
    dispatch(buyVendorCart(group.items, history, routeConfiguration, config)).catch(e => {
      setVendorBusy(null);
      if (e?.code === SELLER_NOT_PAYABLE) setNotPayable(true);
    });
  };

  const handleRemove = (e, listingId) => {
    e.preventDefault();
    e.stopPropagation();
    removeFromCart(listingId);
  };

  return (
    <Menu className={className}>
      <MenuLabel className={css.menuLabel}>
        <div
          className={iconClassName || css.iconSlot}
          aria-label={count > 0 ? `Varukorg, ${count} annonser` : 'Varukorg'}
          data-tooltip="Varukorg"
          role="button"
          tabIndex={0}
        >
          <CartIcon />
          {badge ? (
            <span className={css.badgeCount} aria-hidden="true">
              {badge}
            </span>
          ) : null}
        </div>
      </MenuLabel>
      <MenuContent className={css.menuContent}>
        {[
          <MenuItem key="cart-header">
            <div className={css.header}>
              <span className={css.headerTitle}>Varukorg</span>
              {count > 0 ? (
                <button type="button" className={css.clearButton} onClick={() => clearCart()}>
                  Töm
                </button>
              ) : null}
            </div>
          </MenuItem>,

          ...(notPayable
            ? [
                <MenuItem key="cart-not-payable" className={css.itemMenuItem}>
                  <div className={css.notPayableNotice}>
                    Säljaren kan inte ta emot betalning via Wheelplace ännu. Kontakta säljaren
                    via annonsen så kommer ni överens om köpet.
                  </div>
                </MenuItem>,
              ]
            : []),

          ...(count > 0
            ? [
                <MenuItem key="cart-viewall" className={css.itemMenuItem}>
                  <NamedLink name="CartPage" className={css.viewAllLink}>
                    Visa hela varukorgen →
                  </NamedLink>
                </MenuItem>,
                ...items.map(item => {
                  const priceLabel = formatItemPrice(
                    intl,
                    item.price,
                    item.orderData?.quantity || 1
                  );
                  const isBuying = buyingId === item.listingId;
                  return (
                    <MenuItem key={item.listingId} className={css.itemMenuItem}>
                      <div className={css.item}>
                        <div className={css.thumb}>
                          {item.image ? (
                            <img src={item.image} alt="" />
                          ) : (
                            <span className={css.thumbFallback} aria-hidden="true">
                              <CartIcon />
                            </span>
                          )}
                        </div>
                        <div className={css.itemBody}>
                          <span className={css.itemTitle}>{item.title}</span>
                          {item.authorName ? (
                            <span className={css.itemSeller}>{item.authorName}</span>
                          ) : null}
                          {/* One package, one line — with what it holds
                            * underneath it and no prices of its own
                            * (P25009-403, BR-1). */}
                          {item.packageParts?.length ? (
                            <ul className={css.packageParts}>
                              {item.packageParts.map((part, i) => (
                                <li key={`${part.listingId || i}-${part.role}`}>
                                  {packagePartLabel(part)}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                          <RowQuantity
                            item={item}
                            onChange={qty => updateCartQuantity(item.listingId, qty)}
                          />
                          <div className={css.itemFooter}>
                            {priceLabel ? (
                              <span className={css.itemPrice}>{priceLabel}</span>
                            ) : (
                              <span />
                            )}
                            <button
                              type="button"
                              className={css.removeButton}
                              aria-label="Ta bort ur varukorgen"
                              onClick={e => handleRemove(e, item.listingId)}
                            >
                              Ta bort
                            </button>
                          </div>
                          <button
                            type="button"
                            className={css.buyButton}
                            disabled={isBuying}
                            onClick={() => handleBuyNow(item)}
                          >
                            {isBuying ? 'Öppnar…' : 'Köp nu'}
                          </button>
                        </div>
                      </div>
                    </MenuItem>
                  );
                }),
                ...vendorList
                  .filter(g => g.items.length > 1)
                  .map(group => {
                    const key = group.authorId || 'unknown';
                    const busy = vendorBusy === key;
                    return (
                      <MenuItem key={`checkout-${key}`} className={css.checkoutMenuItem}>
                        <button
                          type="button"
                          className={css.vendorCheckoutButton}
                          disabled={busy}
                          onClick={() => handleVendorCheckout(group)}
                        >
                          {busy
                            ? 'Öppnar…'
                            : `Gå till kassan${
                                group.authorName ? ` · ${group.authorName}` : ''
                              } (${group.items.length} varor)`}
                        </button>
                      </MenuItem>
                    );
                  }),
              ]
            : [
                <MenuItem key="cart-empty">
                  <div className={css.empty}>
                    <span className={css.emptyIcon} aria-hidden="true">
                      <CartIcon />
                    </span>
                    <span className={css.emptyTitle}>Varukorgen är tom</span>
                    <span className={css.emptyLead}>
                      Lägg till annonser från en annonssida så dyker de upp här.
                    </span>
                  </div>
                </MenuItem>,
              ]),
        ].filter(Boolean)}
      </MenuContent>
    </Menu>
  );
};

export default CartMenu;
